import { useEffect, useState } from "react";
import type { Field, Row } from "../types";
import { IconClose } from "../icons";
import { ordenarItens } from "../ordem";

interface Props {
  title: string;
  fields: Field[];
  initial?: Row | null;
  itens: Row[]; // para selects com ref ao catálogo de itens
  onCancel: () => void;
  onSubmit: (data: Row) => Promise<void>;
}

/** Modal de cadastro/edição gerado a partir da definição de campos. */
export default function RecordForm({
  title,
  fields,
  initial,
  itens,
  onCancel,
  onSubmit,
}: Props) {
  const [form, setForm] = useState<Row>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Campos calculados no banco (ex.: saldo, divergência, lead time) não são editáveis.
  const editable = fields.filter((f) => !f.computed);

  useEffect(() => {
    const base: Row = {};
    for (const f of editable) {
      let v = initial ? initial[f.name] : "";
      if (f.type === "date" && v) v = String(v).slice(0, 10); // YYYY-MM-DD
      base[f.name] = v ?? "";
    }
    setForm(base);
    setError(null);
  }, [initial, fields]);

  const set = (name: string, value: unknown) =>
    setForm((prev) => ({ ...prev, [name]: value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit(form);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onCancel}>
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{title}</h3>
          <button type="button" className="icon-btn" onClick={onCancel}>
            <IconClose size={18} />
          </button>
        </div>

        {/* O rodapé fica FORA do form (preso embaixo, sem entrar na rolagem):
            o botão Salvar continua enviando este form pelo atributo `form`. */}
        <form id="record-form" className="modal-body" onSubmit={submit}>
          <div className="form-grid">
            {editable.map((f) => (
              <label
                key={f.name}
                className={f.type === "textarea" ? "field field-wide" : "field"}
              >
                <span>
                  {f.label}
                  {f.required && <em className="req"> *</em>}
                </span>
                {renderInput(f, form[f.name], set, itens)}
                {f.help && <small className="field-help">{f.help}</small>}
              </label>
            ))}
          </div>

        </form>

        <div className="modal-foot">
          {error && <div className="form-error">{error}</div>}
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancelar
          </button>
          <button type="submit" form="record-form" className="btn-primary" disabled={saving}>
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function renderInput(
  f: Field,
  value: unknown,
  set: (name: string, value: unknown) => void,
  itens: Row[],
) {
  const v = (value ?? "") as string | number;

  if (f.ref === "itens") {
    return (
      <select
        value={v}
        required={f.required}
        onChange={(e) => set(f.name, e.target.value)}
      >
        <option value="">Selecione...</option>
        {/* Cópia ordenada A→Z pela descrição — a lista do App não é alterada. */}
        {ordenarItens(itens).map((i) => (
          <option key={i.id} value={i.id}>
            {i.codigo ? `${i.codigo} — ${i.descricao}` : i.descricao}
          </option>
        ))}
      </select>
    );
  }

  switch (f.type) {
    case "select":
    case "status":
      return (
        <select
          value={v}
          required={f.required}
          onChange={(e) => set(f.name, e.target.value)}
        >
          <option value="">Selecione...</option>
          {f.options?.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    case "textarea":
      return (
        <textarea
          rows={3}
          value={v}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "number":
      return (
        <input
          type="number"
          step="any"
          value={v}
          required={f.required}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "currency":
      return (
        <input
          type="number"
          step="0.01"
          min="0"
          value={v}
          required={f.required}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "date":
      return (
        <input
          type="date"
          value={v}
          required={f.required}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "email":
      return (
        <input
          type="email"
          value={v}
          required={f.required}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "password":
      return (
        <input
          type="password"
          value={v}
          required={f.required}
          placeholder={f.placeholder}
          autoComplete="new-password"
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    default:
      return (
        <input
          type="text"
          value={v}
          required={f.required}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
  }
}
